import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import TourSearchSelectRHF from '@/components/asyncSelect/entitySelects/TourSearchSelectRHF.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import { EmptyValue } from '@/components/ui/EmptyValue.tsx';
import useQualifyAsExpense from '@/features/dashboard/finances/hooks/useQualifyAsExpense.ts';
import useMatchToExpense from '@/features/dashboard/finances/hooks/useMatchToExpense.ts';
import useGetCostCategoryOptions from '@/features/dashboard/expenses/hooks/useGetCostCategoryOptions.ts';
import expensesApi from '@/features/dashboard/expenses/api/expensesApi.ts';
import { expensesApiQueryKeys } from '@/features/dashboard/expenses/api/expensesApi.queryKeys.ts';
import { emptyFilterCriteria } from '@/components/table/types/FilterCriteriaDto.tsx';
import { RawTransactionListDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionListDto.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { formatPrice } from '@/utils/formatters.ts';
import Option from '@/components/form/types/Option.ts';
import { optionalPositiveNumberSchema } from '@/schemas/optionalNumberSchema.ts';

enum QualifyMode {
  NEW = 'new',
  EXISTING = 'existing',
}

const modeOptions: Option[] = [
  { value: QualifyMode.NEW, label: 'Nowy wydatek' },
  { value: QualifyMode.EXISTING, label: 'Istniejący wydatek' },
];

const qualifySchema = z
  .object({
    mode: z.enum(QualifyMode).default(QualifyMode.NEW),
    tourId: z.string().min(1, 'Wyjazd jest wymagany'),
    costCategoryId: z.string().default(''),
    description: z.string().default(''),
    allocatedAmount: optionalPositiveNumberSchema(
      'Kwota musi być większa niż 0',
    ),
    notes: z.string().default(''),
    expenseId: z.string().default(''),
  })
  .superRefine((data, ctx) => {
    // Kategoria jest wymagana tylko przy tworzeniu nowego wydatku —
    // przy dopasowaniu bierze się z wydatku, w który wpinamy przelew.
    if (data.mode === QualifyMode.NEW && !data.costCategoryId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Kategoria kosztów jest wymagana',
        path: ['costCategoryId'],
      });
    }
    if (data.mode === QualifyMode.EXISTING && !data.expenseId) {
      ctx.addIssue({
        code: 'custom',
        message: 'Wybierz wydatek',
        path: ['expenseId'],
      });
    }
  });

type QualifyFormData = z.infer<typeof qualifySchema>;
type QualifyInputData = z.input<typeof qualifySchema>;

interface QualifyAsExpenseModalProps {
  transaction: RawTransactionListDto;
  onClose: () => void;
}

const QualifyAsExpenseModal = ({
  transaction,
  onClose,
}: QualifyAsExpenseModalProps) => {
  const qualifyMutation = useQualifyAsExpense();
  const matchMutation = useMatchToExpense();
  const { options: costCategoryOptions } = useGetCostCategoryOptions();

  // Przelew kosztowy jest ujemny, a operator podaje kwotę dodatnią.
  const transactionAmount = Math.abs(transaction.amount.amount);
  const transactionCurrency = transaction.amount.currency ?? Currency.PLN;

  const methods = useForm<QualifyInputData>({
    resolver: zodResolver(qualifySchema),
    defaultValues: {
      mode: QualifyMode.NEW,
      tourId: '',
      costCategoryId: '',
      description: transaction.description || '',
      allocatedAmount: undefined,
      notes: '',
      expenseId: '',
    },
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = methods;

  const mode = watch('mode');
  const tourId = watch('tourId');

  const { data: tourExpenses } = useQuery({
    queryKey: [...expensesApiQueryKeys.byTourId(tourId), 'options'],
    queryFn: () =>
      expensesApi.getAll(tourId)({ page: 0, size: 100 }, emptyFilterCriteria),
    enabled: mode === QualifyMode.EXISTING && !!tourId,
  });

  const expenseOptions: Option[] =
    tourExpenses?.content?.map((expense) => ({
      value: expense.id,
      label: `${expense.costCategoryName} — ${formatPrice(expense.amount.amount, expense.amount.currency)} (${expense.dueDate})`,
    })) ?? [];

  const onSubmit = (data: QualifyFormData) => {
    const amount = data.allocatedAmount;
    const allocatedAmount =
      amount === undefined || Number.isNaN(amount)
        ? undefined
        : { amount, currency: transactionCurrency };

    if (data.mode === QualifyMode.NEW) {
      qualifyMutation.mutate(
        {
          id: transaction.id,
          request: {
            tourId: data.tourId,
            costCategoryId: data.costCategoryId,
            description: data.description || undefined,
            allocatedAmount,
            notes: data.notes || undefined,
          },
        },
        { onSuccess: () => onClose() },
      );
      return;
    }

    matchMutation.mutate(
      {
        id: transaction.id,
        request: {
          expenseId: data.expenseId,
          allocatedAmount,
          notes: data.notes || undefined,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  const isPending = qualifyMutation.isPending || matchMutation.isPending;

  return (
    <Modal onClose={onClose} isDirty={isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Zakwalifikuj jako wydatek" onClose={onClose} />
          <ModalContent>
            <div className="space-y-1 rounded-md bg-neutral-50 p-3">
              <InfoCardList className="flex-1 space-y-1">
                <InfoCardRow layout="columns" label="Kwota">
                  {formatPrice(transactionAmount, transactionCurrency)}
                </InfoCardRow>
                <InfoCardRow layout="columns" label="Data transakcji">
                  {dayjs(transaction.transactionDate).format('DD.MM.YYYY')}
                </InfoCardRow>
                <InfoCardRow layout="columns" label="Kontrahent">
                  {transaction.counterpartyName || <EmptyValue />}
                </InfoCardRow>
                {transaction.description && (
                  <InfoCardRow layout="columns" label="Opis">
                    {transaction.description}
                  </InfoCardRow>
                )}
              </InfoCardList>
            </div>

            <ToggleInput
              fieldPath="mode"
              label="Tryb"
              options={modeOptions}
              control={control}
              required
            />

            <TourSearchSelectRHF
              fieldPathName="tourId"
              control={control}
              label="Wyjazd"
              required
              error={errors.tourId?.message}
              placeholder="Wyszukaj wyjazd..."
            />

            {mode === QualifyMode.NEW && (
              <>
                <SingleSelectInput
                  fieldName="costCategoryId"
                  control={control}
                  options={costCategoryOptions}
                  label="Kategoria kosztów"
                  required
                  error={errors.costCategoryId?.message}
                  placeholder="Wybierz kategorię..."
                />

                <TextareaInput
                  label="Opis wydatku"
                  register={register('description')}
                  error={errors.description?.message}
                  placeholder="Puste = nazwa kontrahenta z wyciągu"
                  rows={2}
                />
              </>
            )}

            {mode === QualifyMode.EXISTING && tourId && (
              <SingleSelectInput
                fieldName="expenseId"
                control={control}
                options={expenseOptions}
                label="Wybierz wydatek"
                required
                error={errors.expenseId?.message}
                placeholder={
                  expenseOptions.length === 0
                    ? 'Brak wydatków dla tego wyjazdu'
                    : 'Wybierz wydatek...'
                }
              />
            )}

            <ValueInput
              label="Kwota do wpięcia"
              register={register('allocatedAmount', { valueAsNumber: true })}
              placeholder="Cały przelew"
              type="number"
              step={0.01}
              error={errors.allocatedAmount?.message}
              sufix={transactionCurrency}
              hint="Zostaw puste, żeby wpiąć cały przelew. Podaj kwotę, żeby rozdzielić go między kilka wydatków."
            />

            <TextareaInput
              label="Notatka"
              register={register('notes')}
              placeholder="Opcjonalna notatka..."
              rows={2}
            />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={isPending}
            >
              {mode === QualifyMode.NEW ? 'Zakwalifikuj' : 'Dopasuj'}
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default QualifyAsExpenseModal;
